import { useState, useEffect, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { ButtonWithSound } from '../Button/Button';
import { API } from 'aws-amplify';
import './ChatChallenge.scss';
import Cover from '../Conversation/cover.jpg'

const ChatChallenge = (props) => {
    const { login } = useSelector((state) => state.loginReducer, shallowEqual);
    
    const generateImageUrl = (baseUrl) => login.cloudFront + baseUrl + login.cookie;

    const [intro, setIntro] = useState(true);
    const [challenge, setChallenge] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [showReminder, setShowReminder] = useState(false);
    const [reminderText, setReminderText] = useState('');
    const [returnToStepIndex, setReturnToStepIndex] = useState(0);
    const [revealedMessages, setRevealedMessages] = useState([]);
    const [score, setScore] = useState(0);
    const [firstTime, setFirstTime] = useState(true);

    const scrollRef = useRef();

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [revealedMessages, showReminder]);

    const currentStep = props.conversationFlow[currentStepIndex];

    const saveChatChallengeResult = async () => {
        try {
            await API.post('TIR', '/v3/student/result', {
            body: {
                student_id: login.studentid,
                metric: `${props.name}_chat_challenge`,
                result: JSON.stringify({
                created: new Date().toISOString(),
                //target is the total type of questions in the conversation flow
                values: [{ value: score, target: props.conversationFlow.filter(step => step.type === 'question').length }],
                }),
            },
            });
        } catch (error) {
            console.log(error);
        }
    };

    const handleNext = () => {
        if (showReminder) {
            // After showing reminder, go back to the step that needs to be repeated
            setShowReminder(false);
            setRevealedMessages([...revealedMessages.slice(0, returnToStepIndex)]);
            setCurrentStepIndex(returnToStepIndex);
        } else {
            // Add current step to revealed messages
            setRevealedMessages([...revealedMessages, currentStep]);
            
            if (currentStepIndex < props.conversationFlow.length - 1) {
                setCurrentStepIndex(currentStepIndex + 1);
            } else {
                // Challenge completed
                saveChatChallengeResult();
                props.finish();
            }
        }
    };

    const handleYes = () => {
        // Add current step to revealed messages
        setRevealedMessages([...revealedMessages, currentStep]);
        if(currentStep.type === 'question' && firstTime){
            setScore(score + 1);
            setFirstTime(true);
        }
        
        // Move to next step
        if (currentStepIndex < props.conversationFlow.length - 1) {
            setCurrentStepIndex(currentStepIndex + 1);
        } else {
            // Challenge completed
            props.finish();
        }
    };

    const handleNo = () => {
        // Show reminder
        setReminderText(currentStep.reminder);
        setReturnToStepIndex(currentStep.returnToStep);
        setShowReminder(true);
        setFirstTime(false);
    };

    return (
        <div className='conversationBody chatChallengeBody'>
            {intro && (
                <div className='mainPage'>
                    <div className='info'>
                        <img className='cover' alt="cover" src={Cover}/>
                        <h2 style={{color: 'darkblue', fontWeight: 600, marginTop: '20px'}}>Chat Challenge</h2>
                        <div className='instructions'>
                            <p style={{fontSize: '1.2rem', margin: '10px 0'}}>
                                <strong>{props.studentName}</strong>, you are going to take the Chat Challenge.
                            </p>
                            <p style={{fontSize: '1.2rem', margin: '10px 0'}}>
                                <strong>{props.friendName}</strong>, take the device and don't let <strong>{props.studentName}</strong> see the screen.
                            </p>
                        </div>
                        <div className="avatars">
                            <div className="avatar">
                                <h4 className="character-name">
                                    You are:
                                </h4>
                                <div className="avatar-inner">
                                    <img alt="student avatar" src={generateImageUrl(props.selectedStudentCharacter.src)}/>
                                </div>
                            </div>
                            <div className="avatar">
                                <h4 className="character-name">
                                    Your friend is:
                                </h4>
                                <div className="avatar-inner">
                                    <img alt="friend avatar" src={generateImageUrl(props.selectedFriendCharacter.src)}/>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='infoButton'>
                        <ButtonWithSound 
                            className='button start' 
                            onClick={() => {setIntro(false); setChallenge(true)}} 
                            label={"LET'S BEGIN!"}
                        />
                    </div>
                </div>
            )}
            
            {challenge && (
                <div className="conversation-builder">
                    <h1 className="conversation-title">{props.topicName}</h1>
                    <p className="conversation-subtitle">Chat Challenge</p>
                    
                    <div className="conversation-area" ref={scrollRef}>
                        {revealedMessages.map((message, index) => (
                            <div
                                key={index}
                                className="message-container message-right"
                            >
                                <div className={`message-bubble message-${props.selectedFriendCharacter.name}`}>
                                    <span className="emoji" role="img" aria-label="friend">
                                        <img alt="friend avatar" src={generateImageUrl(props.selectedFriendCharacter.src)}/>
                                    </span>
                                    <span className="message-text">{message.text}</span>
                                </div>
                            </div>
                        ))}

                        {showReminder && (
                            <div className="message-container message-right">
                                <div className={`message-bubble message-${props.selectedFriendCharacter.name}`} style={{backgroundColor: '#ef4444'}}>
                                    <span className="emoji" role="img" aria-label="friend">
                                        <img alt="friend avatar" src={generateImageUrl(props.selectedFriendCharacter.src)}/>
                                    </span>
                                    <span className="message-text">{reminderText}</span>
                                </div>
                            </div>
                        )}

                        {!showReminder && currentStep && (
                            <div className="message-container message-right">
                                <div className={`message-bubble message-${props.selectedFriendCharacter.name}`} style={{backgroundColor: currentStep.type === 'question' ? '#3b82f6' : '#fbbf24'}}>
                                    <span className="emoji" role="img" aria-label="friend">
                                        <img alt="friend avatar" src={generateImageUrl(props.selectedFriendCharacter.src)}/>
                                    </span>
                                    <span className="message-text">{currentStep.text}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='character-container'>
                        <div className='left'>
                            <img alt="student character" src={generateImageUrl(props.selectedStudentCharacter.full)}/>
                        </div>
                        <div className="middle">
                            {showReminder || currentStep.type === 'instruction' ? (
                                <ButtonWithSound
                                    button={true}
                                    onClick={handleNext}
                                    className="next-button"
                                    label={showReminder ? "Next" : "Next"}
                                />
                            ) : (
                                <div className="validation-buttons">
                                    <ButtonWithSound
                                        button={true}
                                        onClick={handleYes}
                                        className="yes-button"
                                        label="Yes"
                                    />
                                    <ButtonWithSound
                                        button={true}
                                        onClick={handleNo}
                                        className="no-button"
                                        label="No"
                                    />
                                </div>
                            )}
                        </div>
                        <div className='right'>
                            <img alt="friend character" src={generateImageUrl(props.selectedFriendCharacter.full)}/>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatChallenge;

